import { pluralize } from "utils/pluralize";
import { Button } from "components/DesignSystem/Button/Button";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { StateRegistrationsSummary } from "store/apis/StateRegistrations";

type SummaryCardsProps = {
  summary: StateRegistrationsSummary;
  onReviewDueFilings: () => void;
  onReviewOverdueFilings: () => void;
};

export const SummaryCardsSkeleton = () => (
  <div className="t-grid t-grid-cols-2 t-gap-4 sm:t-grid-cols-1 sm:t-gap-3">
    <div className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
      <Skeleton className="t-h-3 t-w-24" />
      <Skeleton className="t-h-7 t-w-10" />
      <div className="t-flex t-items-center t-gap-4">
        <Skeleton className="t-h-3 t-w-24" />
        <Skeleton className="t-h-3 t-w-20" />
      </div>
    </div>
    <div className="t-flex t-flex-col t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
      <Skeleton className="t-h-3 t-w-28" />
      <div className="t-flex t-items-center t-justify-between t-gap-3">
        <Skeleton className="t-h-3 t-w-32" />
        <Skeleton className="t-h-3 t-w-12" />
      </div>
      <div className="t-flex t-items-center t-justify-between t-gap-3">
        <Skeleton className="t-h-3 t-w-32" />
        <Skeleton className="t-h-3 t-w-12" />
      </div>
    </div>
  </div>
);

const StatDot = ({
  colorClassName,
  label,
}: {
  colorClassName: string;
  label: string;
}) => (
  <span className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-30 sm:t-whitespace-nowrap">
    <span
      className={`t-w-1.5 t-h-1.5 t-shrink-0 t-rounded-full ${colorClassName}`}
    />
    {label}
  </span>
);

export const SummaryCards = ({
  summary,
  onReviewDueFilings,
  onReviewOverdueFilings,
}: SummaryCardsProps) => {
  const {
    total_states: totalStates,
    not_registered_count: notRegisteredCount,
    filing_in_progress_count: filingInProgressCount,
    missing_info_count: missingInfoCount,
    registered_count: registeredCount,
    overdue_filings_count: overdueFilingsCount,
    upcoming_filings_count: upcomingFilingsCount,
  } = summary;

  const overdueCount = overdueFilingsCount ?? 0;
  const upcomingCount = upcomingFilingsCount ?? 0;

  const hasOverdues = overdueCount > 0;
  const hasUpcoming = upcomingCount > 0;
  const hasDeadlines = hasOverdues || hasUpcoming;
  const totalFilingsDue = overdueCount + upcomingCount;

  return (
    <div
      className={`t-grid t-gap-4 sm:t-grid-cols-1 sm:t-gap-3 ${
        hasDeadlines ? "t-grid-cols-2" : "t-grid-cols-1"
      }`}
    >
      <div className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
        <p className="t-m-0 t-text-body t-text-text-30">States tracked</p>
        <span className="t-text-title-h2 t-text-text-100">{totalStates}</span>
        <div className="t-flex t-items-center t-gap-4 sm:t-flex-wrap sm:t-gap-x-4 sm:t-gap-y-1">
          {notRegisteredCount > 0 && (
            <StatDot
              colorClassName="t-bg-red-50"
              label={`${notRegisteredCount} ${pluralize(
                notRegisteredCount,
                "Not registered",
                "Not registered",
                false
              )}`}
            />
          )}
          {filingInProgressCount > 0 && (
            <StatDot
              colorClassName="t-bg-orange-70"
              label={`${filingInProgressCount} ${pluralize(
                filingInProgressCount,
                "Filing in progress",
                "Filings in progress",
                false
              )}`}
            />
          )}
          {missingInfoCount > 0 && (
            <StatDot
              colorClassName="t-bg-orange-70"
              label={`${missingInfoCount} Missing info`}
            />
          )}
          {registeredCount > 0 && (
            <StatDot
              colorClassName="t-bg-dark_green"
              label={`${registeredCount} Registered`}
            />
          )}
        </div>
      </div>

      {hasDeadlines && (
        <div className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
          <p className="t-m-0 t-text-body t-text-text-30">Filing Due</p>
          <span className="t-text-title-h2 t-text-text-100">
            {totalFilingsDue}
          </span>
          <div className="t-flex t-items-center t-gap-4">
            {hasOverdues && (
              <Button
                type="button"
                customType="link"
                onClick={onReviewOverdueFilings}
              >
                <StatDot
                  colorClassName="t-bg-red-50"
                  label={`${overdueCount} overdue`}
                />
              </Button>
            )}
            {hasUpcoming && (
              <Button
                type="button"
                customType="link"
                onClick={onReviewDueFilings}
              >
                <StatDot
                  colorClassName="t-bg-yellow-50"
                  label={`${upcomingCount} due`}
                />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
